/**
 * The airspace check, read unattended, exactly as the Strecke panel reads it.
 *
 * It is deliberately a second caller of `POST /api/route/check` rather than a
 * lift of the panel's state into a provider: `map/RoutePanel.tsx` is not this
 * stage's file, and a shared provider is a change to the workspace's provider
 * stack. The contract is copied instead of the code — same 500 ms debounce, same
 * `checkAltitudes` (the map's own `alt_ft` first, then the Höhe/FL cell of the
 * leg that ends there, then the server's default), same abort on every change,
 * same "an aborted fetch is not an error". Two panes therefore ask the same
 * question and get the same answer; only one of them is ever mounted, because
 * the inspector renders the active tab alone.
 *
 * When the check moves into a provider (it should — see the stage report), this
 * hook is the thing to delete and the two callers become `useAirspace()`.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import * as api from '../../api';
import { usePlanRev, usePlanStore, useRoute } from '../../planContext';
import { DEFAULT_CHECK_ALT_FT, checkAltitudes } from '../../route';
import { airspaceLegs, type AirspaceLeg } from './airspace';

/** One gesture is a dozen commits; the check waits for the hand to stop. */
const DEBOUNCE_MS = 500;

export interface AirspaceView {
  /** one entry per leg, in route order; empty before the first answer */
  legs: AirspaceLeg[];
  /** the codes the endpoint publishes about what the answer does NOT cover */
  assumptions: string[];
  busy: boolean;
  error: unknown;
  /** true once an answer has arrived for the current route */
  ready: boolean;
}

export function useAirspace(): AirspaceView {
  const store = usePlanStore();
  const rev = usePlanRev();
  const route = useRoute();
  const [check, setCheck] = useState<api.RouteCheck | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const shownKey = useRef('');

  const waypoints = useMemo(() => route?.waypoints ?? [], [route]);

  /**
   * Half of this comes out of the FORM: typing into the Höhe/FL column changes
   * the altitude the check runs at. `rev` is the store's change signal; the key
   * below de-duplicates, so a keystroke that changes no altitude costs nothing.
   */
  const points = useMemo<api.RouteCheckPoint[]>(() => {
    const alts = checkAltitudes(waypoints, leg => store.val('leg' + leg + '_alt'));
    return waypoints.map((w, i) => {
      const a = alts[i];
      return { lat: w.lat, lon: w.lon, ...(a != null ? { alt_ft: a } : {}) };
    });
  }, [waypoints, store, rev]);

  const key = useMemo(() => JSON.stringify(points), [points]);

  useEffect(() => {
    if (points.length < 2) {
      setCheck(null);
      setError(null);
      setBusy(false);
      shownKey.current = key;
      return;
    }
    const ctl = new AbortController();
    const timer = setTimeout(() => {
      setBusy(true);
      setError(null);
      void api
        .checkRoute(points, { defaultAltFt: DEFAULT_CHECK_ALT_FT, signal: ctl.signal })
        .then(res => {
          setCheck(res);
          shownKey.current = key;
          setBusy(false);
        })
        .catch((e: unknown) => {
          if (api.isAbort(e)) return;
          setError(e);
          setCheck(null);
          setBusy(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      ctl.abort();
    };
    // `key` is the value identity of `points`: re-running on the array identity
    // alone would fire the check on every unrelated store notification
  }, [key]);

  const legs = useMemo(() => airspaceLegs(check), [check]);

  return {
    legs,
    assumptions: check?.assumptions ?? [],
    busy,
    error,
    ready: check !== null && shownKey.current === key,
  };
}
